import { useQuery, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAnalytics } from 'use-analytics';
import { HttpRequestAIActionFromTextDialog } from '../../Action/HttpRequestActionFromText/HttpRequestActionFromText.dialog';
import { useSocket } from '../../SocketProvider';
import { SubscriptionProvider } from '../../SubscriptionProvider';
import { QueryKey } from '@/Config/api/queryKey';
import { SocketKey } from '@/Config/api/socketKey';
import i18n from '@/Config/app/i18n';
import { IndexPath, SettingsTabsEnum } from '@/Config/app/router/index.path';
import { pushGtmEvent } from '@/Config/libs/analytics';
import {
  ActionDto,
  HttpRequestAIActionFromTextMode,
  HttpRequestAIActionFromTextEmitProps,
} from '@/Domain/api/Action/dto/Action.dto';
import { AccountDto } from '@/Domain/api/User/dto/Account.dto';
import { KeepAliveRenderer } from '@/Domain/components/App/KeepAliveRenderer';
import { ShopifyReviewPrompt } from '@/Domain/components/App/ShopifyReviewPrompt/ShopifyReviewPrompt';
import { AppSidebar } from '@/Domain/components/App/Sidebar/AppSidebar';
import { SupportWidget } from '@/Domain/components/App/SupportWidget';
import { AccountMcpConnectSheet } from '@/Domain/components/Integration/AccountMcpConnect/AccountMcpConnect.sheet';
import { pickOAuthCallbackSearch } from '@/Domain/components/Integration/useIntegrationOAuthCallback';
import { StatusUploadFiles } from '@/Domain/components/KnowledgeBase/StatusUploadFiles/StatusUploadFiles.modal';
import { CloneJobWatchers } from '@/Domain/components/Settings/ai/CloneJobWatchers';
import { SidebarInset, SidebarProvider } from '@/Domain/components/Shared/Sidebar/Sidebar';
import { AgentMutedBanner } from '@/Domain/components/Subscription/AgentMutedBanner/AgentMutedBanner';
import { useDiscordCreditsNotice } from '@/Domain/components/Subscription/CreditTopup/useDiscordCreditsNotice.hook';
import { TopBar } from '@/Domain/components/TopBar/TopBar';
import { GtmEventEnum } from '@/Domain/enum/GtmEvent.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { useSuggestLanguageSwitch } from '@/Domain/hooks/App/useDetectLanguage.hook';
import { usePermissions } from '@/Domain/hooks/App/usePermissions.hook';
import { usePageviewPostHog } from '@/Domain/hooks/App/usePostHog/usePageviewPostHog.hook';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';
import { useUser } from '@/Domain/hooks/User/useUser.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { readSignupAttribution } from '@/Domain/utils/signupAttribution';
import { AiPreviewPage } from '@/UI/AiPreview/AiPreview.page';
import { MobilePreviewDialog } from '@/UI/AiPreview/MobilePreviewDialog';
import { useOnboardingChromeHidden } from '@/UI/GettingStarted/components/onboardingChromeStore';
import { useIsGettingStartedComplete } from '@/UI/GettingStarted/components/useGettingStartedOnboarding';
import { SiteScrapeReviewModal } from '@/UI/KnowledgeBase/SiteScrapeReview/SiteScrapeReview.modal';
import { SiteScrapeStatusDock } from '@/UI/KnowledgeBase/SiteScrapeReview/SiteScrapeStatusDock';
import { SCENARIO_ID } from '@/context';

enum ScrapeItemState {
  PENDING = 'PENDING',
  INVALID = 'INVALID',
  DONE = 'DONE',
  ERROR = 'ERROR',
}

interface ScrapeItemUpdatedEmitProps {
  scenario_id: string;
  url: string;
  state: ScrapeItemState;
  toast_process_uuid: string | null;
}

function parseTierInfo(raw: string | null): Record<string, unknown> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    const { from_tier, to_tier, interval, source, value, currency } = parsed as Record<
      string,
      unknown
    >;
    return { from_tier, to_tier, interval, source, value, currency };
  } catch {
    return {};
  }
}

const ONBOARDING_POST_CHECKOUT_FLAG = '__qc_onboarding_post_checkout';

type Props = {
  withPadding?: boolean;
};

export default function DefaultLayout({ withPadding = true }: Props) {
  const { identify, page } = useAnalytics();

  const { isOpen, toggleModal, setParams } = useModalStore();
  const navigate = useNavigate();
  const { scenario } = useParams<{ scenario: string }>();
  const { socket } = useSocket();

  const { user } = useUser();
  const postHog = usePostHog();
  const location = useLocation();
  const queryClient = useQueryClient();
  const account = queryClient.getQueryData<AccountDto>(['account']);
  const subscriptionQuery = useGetSubscription();

  useSuggestLanguageSwitch();
  usePermissions();
  useEffect(() => {
    if (account != null && user != null) {
      identify(account.user.id, {
        email: account.user.email,
        $email: account.user.email,
        USER_ID: account.user.id,
        id: account.user.id,
      });

      postHog.identify(account.user.id.toString(), {
        $user_id: account.user.id,
        id: account.user.id,
        email: account.user.email,
        scenario_id: SCENARIO_ID,
        created_at: new Date(user.created_at),
        trial: subscriptionQuery.data?.current_plan.tier,
        ui_language: i18n.resolvedLanguage,
      });

      // Group by Scenario (AI Agent)
      postHog.group('scenario', SCENARIO_ID, {
        tier: subscriptionQuery.data?.current_plan.tier,
        interval: subscriptionQuery.data?.current_plan.interval,
      });

      // Group by Organization (User Account)
      postHog.group('organization', account.user.id.toString(), {
        email_domain: account.user.email.split('@')[1],
        scenarios_count: account.scenarios.length,
        tier: subscriptionQuery.data?.current_plan.tier,
      });

      // Set person properties for feature flags
      postHog.setPersonPropertiesForFlags({
        tier: subscriptionQuery.data?.current_plan.tier,
        scenarios_count: account.scenarios.length,
      });
    }
  }, [account, user, postHog, subscriptionQuery]);

  // GTM: fire subscription_started when returning from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') !== 'success') return;

    const tierInfoStr = sessionStorage.getItem('__qc_checkout_tier_info');
    const { from_tier, to_tier, interval, source, value, currency } = parseTierInfo(tierInfoStr);
    const tier = subscriptionQuery.data?.current_plan.tier;

    // Onboarding return is its own legitimacy signal — sessionStorage is
    // same-origin so bots can't trip it. Skipping this branch lets Stripe's
    // webhook race trap automatic-mode users on /getting-started.
    const isOnboardingReturn = source === 'getting_started';
    const hasPaidTier = tier != null && tier !== TierEnum.TRIAL && tier !== TierEnum.FREE;
    if (!hasPaidTier && !isOnboardingReturn) return;

    sessionStorage.removeItem('__qc_checkout_tier_info');

    // Enrich the GTM Google Ads purchase conversion: list-price revenue (captured at checkout start), a unique txn id (Stripe session), and stored click ids so Google can credit the ad click.
    const transactionId = params.get('session_id') ?? undefined;
    const attribution = readSignupAttribution();

    pushGtmEvent(GtmEventEnum.SubscriptionStarted, {
      scenario_id: SCENARIO_ID,
      ...(from_tier && { from_tier }),
      ...(to_tier && { to_tier }),
      ...(interval && { interval }),
      ...(value != null && { value, currency }),
      ...(transactionId && { transaction_id: transactionId }),
      ...(attribution?.gclid && { gclid: attribution.gclid }),
      ...(attribution?.gbraid && { gbraid: attribution.gbraid }),
      ...(attribution?.wbraid && { wbraid: attribution.wbraid }),
    });

    // Onboarding checkout: hand off to /getting-started so the standard celebration fires.
    if (isOnboardingReturn && scenario) {
      sessionStorage.setItem(ONBOARDING_POST_CHECKOUT_FLAG, '1');
      navigate(`/i/${scenario}/${IndexPath.gettingStarted}`, { replace: true });
      return;
    }

    // Clean the query params from the URL without a page reload
    params.delete('checkout');
    params.delete('session_id');
    navigate({ search: params.toString() }, { replace: true });
  }, [location.search, navigate, subscriptionQuery.data?.current_plan.tier, scenario]);

  // GTM: fire app_session_start once per browser session, and long_time_no_see if applicable
  const gtmSessionFired = useRef(false);
  useEffect(() => {
    if (gtmSessionFired.current || account == null || user == null) return;
    const tier = subscriptionQuery.data?.current_plan.tier;
    if (tier == null) return;

    gtmSessionFired.current = true;

    const SESSION_KEY = '__qc_gtm_session_start';
    if (!sessionStorage.getItem(SESSION_KEY)) {
      sessionStorage.setItem(SESSION_KEY, '1');
      pushGtmEvent(GtmEventEnum.AppSessionStart, {
        user_id: account.user.id,
        scenario_id: SCENARIO_ID,
        tier,
      });

      // long_time_no_see: check if user has been absent for a significant period
      const lastVisitKey = `__qc_last_visit_${SCENARIO_ID}`;
      const lastVisitStr = localStorage.getItem(lastVisitKey);
      const now = Date.now();
      localStorage.setItem(lastVisitKey, now.toString());

      if (lastVisitStr) {
        const lastVisit = parseInt(lastVisitStr, 10);
        const createdAt = new Date(user.created_at).getTime();
        const daysSinceCreation = (now - createdAt) / (1000 * 60 * 60 * 24);
        const daysAbsent = (now - lastVisit) / (1000 * 60 * 60 * 24);

        if (daysSinceCreation >= 1 && daysAbsent / daysSinceCreation > 0.5) {
          pushGtmEvent(GtmEventEnum.LongTimeNoSee, {
            user_id: account.user.id,
            scenario_id: SCENARIO_ID,
            days_absent: Math.round(daysAbsent),
            days_since_creation: Math.round(daysSinceCreation),
          });
        }
      }
    }
  }, [account, user, subscriptionQuery.data?.current_plan.tier]);

  // GTM: fire first_live_conversation milestone (once ever per scenario)
  const milestoneFlagKey = `__qc_gtm_milestone_${SCENARIO_ID}_${GtmEventEnum.FirstLiveConversation}`;
  // Skip the query if first_live_conversation already fired, or if the user is on
  // custom tier (enterprise customers with high traffic, never targeted by Meta Ads)
  const shouldSkipMilestoneQuery =
    localStorage.getItem(milestoneFlagKey) != null ||
    subscriptionQuery.data?.current_plan.tier === TierEnum.CUSTOM;

  const hasConversationsQuery = useQuery<boolean>({
    queryKey: [QueryKey.conversationSources],
    enabled: account != null && !shouldSkipMilestoneQuery,
  });
  // Intentionally one-shot: milestone check runs once per mount.
  // A page refresh re-evaluates; localStorage flags provide persistence.
  const gtmMilestonesFired = useRef(false);
  useEffect(() => {
    if (gtmMilestonesFired.current || hasConversationsQuery.data == null) return;
    gtmMilestonesFired.current = true;

    if (!hasConversationsQuery.data) return;

    if (!localStorage.getItem(milestoneFlagKey)) {
      localStorage.setItem(milestoneFlagKey, '1');
      pushGtmEvent(GtmEventEnum.FirstLiveConversation, { scenario_id: SCENARIO_ID });
    }
  }, [hasConversationsQuery.data]);

  usePageviewPostHog();

  useEffect(() => {
    page({
      path: location.pathname,
      search: location.search,
      url: window.location.origin + location.pathname + location.search + location.hash,
    });
  }, [location]);

  useEffect(() => {
    const showDetailsModal = (data: HttpRequestAIActionFromTextEmitProps) => {
      const httpRequestActionFromText = data.data;

      if (!isOpen(MODAL_NAMES.HTTP_REQUEST_AI_ACTION_FROM_TEXT_MODAL)) {
        toggleModal(MODAL_NAMES.HTTP_REQUEST_AI_ACTION_FROM_TEXT_MODAL, {
          mode: data.mode,
          httpRequestAIActionFromText: httpRequestActionFromText,
        });
      } else {
        setParams({
          mode: data.mode,
          httpRequestAIActionFromText: httpRequestActionFromText,
        });
      }

      // Navigate onSuccess if no protected modals where open
      if (
        isOpen(MODAL_NAMES.HTTP_REQUEST_AI_ACTION_FROM_TEXT_MODAL) &&
        data.mode == HttpRequestAIActionFromTextMode.CREATED
      ) {
        navigate(`/i/${scenario}/${IndexPath.actions}`, { viewTransition: true });
      }
    };

    const handleHttpRequestActionFromTextCreated = (data: HttpRequestAIActionFromTextEmitProps) => {
      toast.dismiss(data.process_id);
      showDetailsModal(data);

      if (data?.data?.http_request_ai_action) {
        const action = data.data.http_request_ai_action;
        if (action) {
          void queryClient.setQueryData([QueryKey.actions.httpRequest.get, action.id], action);
          queryClient.setQueryData<ActionDto[]>([QueryKey.actions.basic.list], (old) => {
            const updateData = old ? [...old, action] : [action];
            return updateData;
          });
        }
      }
    };

    socket.on(SocketKey.actions.httpRequestActionFromText, handleHttpRequestActionFromTextCreated);
    return () => {
      socket.off(
        SocketKey.actions.httpRequestActionFromText,
        handleHttpRequestActionFromTextCreated,
      );
    };
  }, [socket]);

  useEffect(() => {
    const handleScrapeItemUpdated = ({ data }: { data: ScrapeItemUpdatedEmitProps }) => {
      if (!data.toast_process_uuid) {
        // TODO: write the logic of moving the ScrapeItems between "sections" of ScrapeProcess window once it's implemented
        return;
      }

      toast.dismiss(data.toast_process_uuid);
      if (data.state == ScrapeItemState.DONE) {
        toast.success(i18n.t(`knowledge_base:import.single_scrape.success`, { url: data.url }));
      } else if (data.state == ScrapeItemState.ERROR) {
        toast.error(i18n.t(`knowledge_base:import.single_scrape.error`, { url: data.url }));
      } else if (data.state == ScrapeItemState.INVALID) {
        toast.error(i18n.t(`knowledge_base:import.single_scrape.invalid`, { url: data.url }));
      }
    };

    socket.on(SocketKey.knowledgeBase.scrape.itemUpdated, handleScrapeItemUpdated);
    return () => {
      socket.off(SocketKey.knowledgeBase.scrape.itemUpdated, handleScrapeItemUpdated);
    };
  }, [socket]);

  // The Getting Started page toggles this via `setOnboardingChromeHidden`:
  // sidebar + top bar are hidden while the user is mid-onboarding, restored
  // once the widget view takes over or they leave the page.
  const chromeHidden = useOnboardingChromeHidden();

  // Gate product setup behind Getting Started while keeping billing reachable.
  //
  // `useIsGettingStartedComplete` reads `/v1/api/getting_started_progress`
  // via React Query. 404 → no row exists (legacy ChatImplementations never
  // got one) → treat as complete so the guard doesn't trap them. While the
  // query is pending we render nothing to avoid a redirect-loop flash.
  const gettingStartedStatus = useIsGettingStartedComplete();
  // Must stay above the getting-started guard: it early-returns a redirect for
  // onboarding-incomplete accounts, which is every account this notice exists for.
  useDiscordCreditsNotice();
  if (scenario) {
    const gettingStartedPath = `/i/${scenario}/${IndexPath.gettingStarted}`;
    const subscriptionPath = `/i/${scenario}/${IndexPath.subscription}`;
    const settingsPath = `/i/${scenario}/${IndexPath.settings}`;
    const onBillingSettings =
      location.pathname === settingsPath &&
      new URLSearchParams(location.search).get('tab') === SettingsTabsEnum.Billing;
    const accessibleWhileIncomplete =
      location.pathname === gettingStartedPath ||
      location.pathname === subscriptionPath ||
      onBillingSettings;
    if (gettingStartedStatus.isLoading) return null;
    if (!accessibleWhileIncomplete && !gettingStartedStatus.isComplete) {
      return (
        <Navigate
          to={{ pathname: gettingStartedPath, search: pickOAuthCallbackSearch(location.search) }}
          replace
        />
      );
    }
  }

  return (
    <SubscriptionProvider>
      <SidebarProvider>
        {!chromeHidden && <AppSidebar />}
        <SidebarInset className="h-svh max-h-svh">
          {!chromeHidden && <TopBar />}
          {!chromeHidden && <AgentMutedBanner />}
          {!chromeHidden && <ShopifyReviewPrompt />}
          <div
            className={classNames(
              // `overscroll-y-none` stops vertical scroll chaining and the
              // browser's rubber-band bounce at the top/bottom of the page,
              // so a scroll past the content edge no longer nudges the whole
              // layout. Scoped to the y-axis so nothing horizontal-scrollable
              // in a page (tables, code blocks, etc.) loses its chaining.
              // Applied here (not per-page) because the layout's
              // `overflow-auto` is the single scroll container every routed
              // page sits inside.
              'flex-1 flex flex-col min-w-0 overflow-auto overscroll-y-none',
              {
                'p-6': withPadding,
                // Onboarding paints the page with `bg-sidebar dark:bg-background`;
                // mirror that on the scroll container so any overflow area
                // (visible when a tall prompt pushes the page past the viewport)
                // matches instead of falling back to the wrong shade.
                'bg-sidebar dark:bg-background': chromeHidden,
              },
            )}
          >
            <Outlet />

            <KeepAliveRenderer
              pathMatch={(pathname) => pathname.includes(IndexPath.aiPreview)}
              component={<AiPreviewPage />}
            />
          </div>

          <SupportWidget />
          <CloneJobWatchers />
          {scenario && <MobilePreviewDialog key={scenario} />}
          <SiteScrapeStatusDock />
          <SiteScrapeReviewModal />
          <StatusUploadFiles />
          <HttpRequestAIActionFromTextDialog />
          <AccountMcpConnectSheet />
        </SidebarInset>
      </SidebarProvider>
    </SubscriptionProvider>
  );
}
